import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CategoriesContext = createContext(null);
const FALLBACK_COLOR = "#B5B5B5";
const RETRY_DELAYS_MS = [2000, 5000, 15000];

export function notifyCategoriesDirty() {
  window.dispatchEvent(new Event("tw1:categories-dirty"));
}

export function CategoriesProvider({ children }) {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const retryTimer = useRef(null);

  const load = useCallback(async (attempt = 0) => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    if (!user) {
      setCategories([]);
      setAllCategories([]);
      setLoading(false);
      setError(false);
      return;
    }
    if (attempt === 0) setLoading(true);
    try {
      const [activeRes, allRes] = await Promise.all([
        api.get("/categories"),
        api.get("/categories", { params: { include_inactive: true } }),
      ]);
      const sortByOrder = (a, b) => a.sort_order - b.sort_order;
      setCategories((activeRes.data || []).slice().sort(sortByOrder));
      setAllCategories((allRes.data || []).slice().sort(sortByOrder));
      setError(false);
      setLoading(false);
    } catch {
      if (attempt < RETRY_DELAYS_MS.length) {
        // FIX#14 (B-1) — keep `loading` true while a retry is still pending, so the
        // "kon niet worden geladen" empty-state doesn't flash on between attempts.
        retryTimer.current = setTimeout(() => load(attempt + 1), RETRY_DELAYS_MS[attempt]);
      } else {
        setError(true);
        setLoading(false);
      }
    }
  }, [user]);

  const reload = useCallback(() => load(0), [load]);

  useEffect(() => { load(0); return () => { if (retryTimer.current) clearTimeout(retryTimer.current); }; }, [load]);

  useEffect(() => {
    const onDirty = () => load(0);
    window.addEventListener("tw1:categories-dirty", onDirty);
    window.addEventListener("focus", onDirty);
    window.addEventListener("online", onDirty);
    return () => {
      window.removeEventListener("tw1:categories-dirty", onDirty);
      window.removeEventListener("focus", onDirty);
      window.removeEventListener("online", onDirty);
    };
  }, [load]);

  const byName = {};
  allCategories.forEach((c) => { byName[c.name] = c; });
  const names = categories.map((c) => c.name);
  const colorOf = (name) => byName[name]?.color || FALLBACK_COLOR;
  const descriptionOf = (name) => byName[name]?.description || "";

  return (
    <CategoriesContext.Provider
      value={{ categories, allCategories, names, byName, colorOf, descriptionOf, loading, error, reload }}
    >
      {children}
    </CategoriesContext.Provider>
  );
}

export function useCategories() {
  const ctx = useContext(CategoriesContext);
  if (!ctx) throw new Error("useCategories must be used inside CategoriesProvider");
  return ctx;
}
